/**
 * LE BANDEAU DE BASCULE — la mention permanente, et la sortie évidente.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  CE COMPOSANT EST UNE EXIGENCE, PAS UNE DÉCORATION
 * ════════════════════════════════════════════════════════════════════════════
 *
 * « Mention permanente et visible à l'écran qu'on consulte le tenant X en tant
 * que superadmin, et sortie de bascule évidente. » Quatre décisions en
 * découlent, toutes contre-intuitives pour qui dessine des interfaces sobres :
 *
 *  1. **Il est fixé en haut de la fenêtre et pousse le contenu**, il ne flotte
 *     pas par-dessus. Un bandeau flottant se fait recouvrir par une modale, et
 *     la modale est justement le moment où l'on croit agir sur ses propres
 *     données.
 *  2. **Il nomme le théâtre en toutes lettres**, pas son slug seul. « baladin »
 *     ne dit rien à 18 h après une journée de support ; « Théâtre du Bourg-Neuf
 *     (baladin) » ne se confond avec rien.
 *  3. **Il dit LECTURE SEULE**, parce que c'est ce que la personne doit savoir
 *     avant de chercher un bouton qui n'existe pas.
 *  4. **La sortie est un bouton plein, dans le bandeau, à droite** — pas une
 *     entrée de menu. Sortir doit demander moins d'effort que rester.
 *
 * Il affiche aussi le **temps restant**, qui décroît. La bascule s'éteint seule
 * au bout de 30 minutes : l'annoncer évite qu'une consultation s'interrompe
 * sans explication au milieu d'un appel.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { Button, useToast } from '../../ui'
import { messageErreur } from '../../lib/api'
import { useFermerBascule, type Bascule } from './api'
import { CHEMINS_CONSOLE } from './chemins'

/** Minutes:secondes restantes, ou `null` si le terme est passé. */
function restant(expireLe: string, maintenant: number): string | null {
  const delta = new Date(expireLe).getTime() - maintenant
  if (delta <= 0) return null
  const secondes = Math.floor(delta / 1000)
  const min = Math.floor(secondes / 60)
  const sec = secondes % 60
  return `${String(min)}:${String(sec).padStart(2, '0')}`
}

export function BandeauBascule({ bascule }: { readonly bascule: Bascule }) {
  const fermer = useFermerBascule()
  const naviguer = useNavigate()
  const toast = useToast()
  const [maintenant, setMaintenant] = useState(() => Date.now())

  /* Une seconde de battement : le compte à rebours doit être vrai, pas
     approximatif — c'est lui qui prévient qu'une consultation va s'arrêter. */
  useEffect(() => {
    const minuteur = window.setInterval(() => {
      setMaintenant(Date.now())
    }, 1000)
    return () => {
      window.clearInterval(minuteur)
    }
  }, [])

  const compte = restant(bascule.expireLe, maintenant)

  async function sortir(): Promise<void> {
    try {
      await fermer.mutateAsync()
      toast.succes(`Sortie de « ${bascule.tenantNom} ».`)
      await naviguer(CHEMINS_CONSOLE.tenant(bascule.tenantSlug), { replace: true })
    } catch (erreur) {
      toast.erreur(messageErreur(erreur, 'Sortie de bascule impossible.'))
    }
  }

  return (
    <div
      /* `role="status"` et non `alert` : c'est un état permanent, pas un
         événement. Un lecteur d'écran l'annonce à l'entrée puis se tait. */
      role="status"
      className="sticky top-0 z-50 border-b-2 border-amber-500 bg-amber-100 text-amber-950 shadow-sm"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5">
        <span
          aria-hidden="true"
          className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-amber-500 text-sm font-bold text-white"
        >
          !
        </span>
        <p className="min-w-0 flex-1 text-sm leading-tight">
          <strong className="font-semibold">
            Vous consultez « {bascule.tenantNom} » ({bascule.tenantSlug}) en tant que superadmin.
          </strong>{' '}
          <span className="whitespace-nowrap rounded bg-amber-200 px-1.5 py-0.5 font-semibold">
            LECTURE SEULE
          </span>{' '}
          <span className="text-amber-900">
            Cette visite est inscrite dans le journal du théâtre. Motif : « {bascule.motif} ».
          </span>
        </p>
        <p className="chiffres-alignes text-sm tabular-nums whitespace-nowrap text-amber-900">
          {compte === null ? 'terme atteint' : `fin dans ${compte}`}
        </p>
        {/* Le retour à l'écran de consultation depuis n'importe où : sans lui,
            un superadmin qui a navigué ailleurs garde une bascule ouverte sans
            savoir comment y revenir — et une bascule ouverte qu'on n'utilise
            pas est un accès aux données d'un client pour rien. */}
        <Link
          to={CHEMINS_CONSOLE.bascule}
          className="rounded-md px-2 py-1 text-sm font-medium text-amber-900 underline underline-offset-2 hover:bg-amber-200"
        >
          Écran de consultation
        </Link>
        <Button
          variante="primaire"
          taille="sm"
          chargement={fermer.isPending}
          onClick={() => {
            void sortir()
          }}
        >
          Sortir de la bascule
        </Button>
      </div>
    </div>
  )
}
